/**
 * What a /projects or /library card opens, and what its "⋯" menu offers.
 * The card is one link (the whole card) plus a labelled main button to the
 * same place, so the user sees where it leads; everything else is in the menu.
 */
export type ListCardType = 'library' | 'project';
export type ListCardActionKind = 'clips' | 'editor' | 'processing' | 'failed' | 'expired';
export type ListCardMenuItem = 'clips' | 'editor' | 'duplicate' | 'downloadOriginal' | 'delete';

export function resolveListCardAction({
  type,
  id,
  status,
  processingMode,
}: {
  type: ListCardType;
  id: string;
  status: string | null | undefined;
  processingMode: string | null | undefined;
}): { kind: ListCardActionKind; href: string | null } {
  const base = `/${type}/${id}`;
  if (status === 'expired') return { kind: 'expired', href: null };
  // The clips page explains a failure and shows the progress while processing.
  if (status === 'failure') return { kind: 'failed', href: `${base}/clips` };
  if (status !== 'success') return { kind: 'processing', href: `${base}/clips` };
  if (processingMode === 'copilot') return { kind: 'editor', href: `${base}/edit` };
  return { kind: 'clips', href: `${base}/clips` };
}

export function buildListCardMenu({
  type,
  status,
  isPublic,
  canDuplicate,
  hasOriginal,
}: {
  type: ListCardType;
  status: string | null | undefined;
  isPublic: boolean;
  canDuplicate: boolean;
  hasOriginal: boolean;
}): ListCardMenuItem[] {
  const items: ListCardMenuItem[] = [];
  if (status === 'success') {
    items.push('clips', 'editor');
    if (canDuplicate && !isPublic) items.push('duplicate');
    if (hasOriginal) items.push('downloadOriginal');
  }
  // Libraries cannot be deleted; a public project is not the user's to delete.
  if (type === 'project' && !isPublic) items.push('delete');
  return items;
}
